// Agent Collaboration and Coordination Service

import {
  AIAgent,
  AgentSpecialization,
  AgentCollaboration,
  AgentCommunication,
  DecisionType,
  AgentTask
} from '../types/aiAgent';

export interface CollaborationRequest {
  initiatorId: string;
  participantIds: string[];
  objective: string;
  type: AgentCollaboration['type'];
  priority: 'low' | 'medium' | 'high' | 'urgent';
  deadline?: Date;
  requiredCapabilities?: string[];
  context?: any;
}

export interface ConflictResolution {
  id: string;
  conflictType: 'resource' | 'decision' | 'priority' | 'capability' | 'strategy';
  involvedAgents: string[];
  description: string;
  resolutionStrategy: 'voting' | 'expertise_based' | 'performance_based' | 'human_arbitration';
  resolution?: {
    decision: any;
    reasoning: string;
    confidence: number;
    timestamp: Date;
    resolvedBy: string;
  };
  status: 'pending' | 'in_progress' | 'resolved' | 'escalated';
  createdAt: Date;
  resolvedAt?: Date;
}

export interface AgentCapabilityMatch {
  agentId: string;
  matchScore: number;
  availableCapabilities: string[];
  missingCapabilities: string[];
  currentLoad: number;
  estimatedContribution: number;
  specialization: AgentSpecialization;
}

export interface CrossAgentLearning {
  id: string;
  sourceAgentId: string;
  targetAgentIds: string[];
  learningType: 'pattern' | 'strategy' | 'failure' | 'optimization';
  knowledge: {
    title: string;
    description: string;
    context: any;
    applicability: string[];
    effectiveness: number;
  };
  transferredAt: Date;
  adoptionStatus: { [agentId: string]: 'pending' | 'adopted' | 'rejected' | 'adapted' };
  validationResults?: { [agentId: string]: any };
}

export interface PerformanceBenchmark {
  agentId: string;
  specialization: AgentSpecialization;
  metrics: {
    successRate: number;
    averageConfidence: number;
    taskCompletionTime: number;
    improvementRate: number;
    collaborationEffectiveness: number;
  };
  ranking: {
    overall: number;
    bySpecialization: number;
    byTaskType: { [taskType: string]: number };
  };
  trends: {
    period: 'daily' | 'weekly' | 'monthly';
    improvement: number;
    consistency: number;
  };
  lastUpdated: Date;
}

export class AgentCollaborationService {
  private collaborations: Map<string, AgentCollaboration> = new Map();
  private conflicts: Map<string, ConflictResolution> = new Map();
  private learningTransfers: Map<string, CrossAgentLearning> = new Map();
  private benchmarks: Map<string, PerformanceBenchmark> = new Map();
  private communicationHistory: AgentCommunication[] = [];

  // Multi-Agent Test Coordination
  async coordinateMultiAgentTest(
    testId: string,
    requiredCapabilities: string[],
    objective: string,
    constraints?: any
  ): Promise<AgentCollaboration> {
    // Find suitable agents for the test
    const suitableAgents = await this.findSuitableAgents(requiredCapabilities, constraints);
    
    if (suitableAgents.length === 0) {
      throw new Error('No suitable agents found for the requested capabilities');
    }

    // Create collaboration
    const collaboration: AgentCollaboration = {
      id: this.generateId(),
      collaboratorIds: suitableAgents.map(agent => agent.agentId),
      type: 'coordination',
      status: 'active',
      objective,
      startedAt: new Date(),
      communications: [],
      sharedResources: [testId],
      outcomes: []
    };

    this.collaborations.set(collaboration.id, collaboration);

    // Assign roles and responsibilities
    await this.assignAgentRoles(collaboration.id, suitableAgents, requiredCapabilities);

    // Set up coordination protocols
    await this.establishCoordinationProtocols(collaboration.id);

    // Initialize shared workspace
    await this.initializeSharedWorkspace(collaboration.id, testId);

    return collaboration;
  }

  // Agent Communication Protocols
  async sendCommunication(
    fromAgentId: string,
    toAgentId: string | string[],
    content: AgentCommunication['content'],
    collaborationId?: string
  ): Promise<AgentCommunication[]> {
    const recipients = Array.isArray(toAgentId) ? toAgentId : [toAgentId];
    const communications: AgentCommunication[] = [];

    for (const recipientId of recipients) {
      const communication: AgentCommunication = {
        id: this.generateId(),
        fromAgentId,
        toAgentId: recipientId,
        timestamp: new Date(),
        type: content.requiresResponse ? 'request' : 'notification',
        content
      };

      // Add to collaboration if specified
      if (collaborationId) {
        const collaboration = this.collaborations.get(collaborationId);
        if (collaboration) {
          collaboration.communications.push(communication);
        }
      }

      // Store in communication history
      this.communicationHistory.push(communication);

      // Process communication
      await this.processCommunication(communication);

      communications.push(communication);
    }

    return communications;
  }

  async broadcastToCollaboration(
    collaborationId: string,
    fromAgentId: string,
    content: AgentCommunication['content']
  ): Promise<AgentCommunication[]> {
    const collaboration = this.collaborations.get(collaborationId);
    if (!collaboration) {
      throw new Error(`Collaboration ${collaborationId} not found`);
    }

    const recipients = collaboration.collaboratorIds.filter(id => id !== fromAgentId);
    return this.sendCommunication(fromAgentId, recipients, content, collaborationId);
  }

  // Knowledge Sharing
  async shareKnowledge(
    sourceAgentId: string,
    knowledge: CrossAgentLearning['knowledge'],
    targetAgentIds?: string[]
  ): Promise<CrossAgentLearning> {
    // Determine target agents if not specified
    const targets = targetAgentIds || await this.findRelevantAgentsForKnowledge(knowledge);

    const learning: CrossAgentLearning = {
      id: this.generateId(),
      sourceAgentId,
      targetAgentIds: targets,
      learningType: this.classifyKnowledgeType(knowledge),
      knowledge,
      transferredAt: new Date(),
      adoptionStatus: targets.reduce((acc, agentId) => {
        acc[agentId] = 'pending';
        return acc;
      }, {} as { [agentId: string]: 'pending' | 'adopted' | 'rejected' | 'adapted' })
    };

    this.learningTransfers.set(learning.id, learning);

    // Notify target agents
    for (const targetId of targets) {
      await this.sendCommunication(sourceAgentId, targetId, {
        message: `Knowledge sharing: ${knowledge.title}`,
        data: learning,
        priority: 'medium',
        requiresResponse: true
      });
    }

    return learning;
  }

  async respondToKnowledgeTransfer(
    learningId: string,
    agentId: string,
    response: 'adopt' | 'reject' | 'adapt',
    adaptations?: any
  ): Promise<void> {
    const learning = this.learningTransfers.get(learningId);
    if (!learning) {
      throw new Error(`Learning transfer ${learningId} not found`);
    }

    learning.adoptionStatus[agentId] = response === 'adapt' ? 'adapted' : response === 'adopt' ? 'adopted' : 'rejected';

    if (response === 'adapt' && adaptations) {
      // Store adaptations for future reference
      if (!learning.validationResults) {
        learning.validationResults = {};
      }
      learning.validationResults[agentId] = adaptations;
    }

    // Update learning effectiveness based on adoption
    await this.updateLearningEffectiveness(learningId);
  }

  // Collaborative Decision Making
  async initiateCollaborativeDecision(
    collaborationId: string,
    decision: {
      type: DecisionType;
      context: any;
      options: any[];
      criteria: string[];
    }
  ): Promise<string> {
    const collaboration = this.collaborations.get(collaborationId);
    if (!collaboration) {
      throw new Error(`Collaboration ${collaborationId} not found`);
    }

    const decisionId = this.generateId();

    // Request input from all collaborators
    for (const agentId of collaboration.collaboratorIds) {
      await this.sendCommunication('system', agentId, {
        message: `Collaborative decision required: ${decision.type}`,
        data: { decisionId, decision },
        priority: 'high',
        requiresResponse: true
      }, collaborationId);
    }

    return decisionId;
  }

  async submitDecisionInput(
    decisionId: string,
    _agentId: string,
    _input: {
      preferredOption: any;
      reasoning: string;
      confidence: number;
      alternativeOptions?: any[];
    }
  ): Promise<void> {
    // Store decision input
    // Implementation would store and aggregate inputs
    
    // Check if all agents have responded
    const allResponded = await this.checkAllAgentsResponded(decisionId);
    
    if (allResponded) {
      await this.resolveCollaborativeDecision(decisionId);
    }
  }

  // Conflict Resolution
  async detectConflict(
    agentIds: string[],
    conflictType: ConflictResolution['conflictType'],
    description: string,
    _context?: any
  ): Promise<ConflictResolution> {
    const conflict: ConflictResolution = {
      id: this.generateId(),
      conflictType,
      involvedAgents: agentIds,
      description,
      resolutionStrategy: this.determineResolutionStrategy(conflictType, agentIds),
      status: 'pending',
      createdAt: new Date()
    };

    this.conflicts.set(conflict.id, conflict);

    // Initiate resolution process
    await this.initiateConflictResolution(conflict.id);

    return conflict;
  }

  async resolveConflict(conflictId: string): Promise<ConflictResolution> {
    const conflict = this.conflicts.get(conflictId);
    if (!conflict) {
      throw new Error(`Conflict ${conflictId} not found`);
    }

    conflict.status = 'in_progress';

    let resolution;
    switch (conflict.resolutionStrategy) {
      case 'voting':
        resolution = await this.resolveByVoting(conflict);
        break;
      case 'expertise_based':
        resolution = await this.resolveByExpertise(conflict);
        break;
      case 'performance_based':
        resolution = await this.resolveByPerformance(conflict);
        break;
      case 'human_arbitration':
        resolution = await this.escalateToHuman(conflict);
        break;
    }

    if (resolution) {
      conflict.resolution = resolution;
      conflict.status = 'resolved';
      conflict.resolvedAt = new Date();

      // Notify involved agents
      await this.notifyConflictResolution(conflict);
    }

    return conflict;
  }

  // Agent Specialization and Capability Matching
  async findSuitableAgents(
    requiredCapabilities: string[],
    constraints?: any
  ): Promise<AgentCapabilityMatch[]> {
    // This would integrate with the main AIAgentService to get available agents
    const availableAgents = await this.getAvailableAgents();
    const matches: AgentCapabilityMatch[] = [];

    for (const agent of availableAgents) {
      const match = await this.calculateCapabilityMatch(agent, requiredCapabilities, constraints);
      if (match.matchScore > 0.5) { // Minimum threshold
        matches.push(match);
      }
    }

    // Sort by match score and availability
    return matches.sort((a, b) => {
      const scoreA = a.matchScore * (1 - a.currentLoad);
      const scoreB = b.matchScore * (1 - b.currentLoad);
      return scoreB - scoreA;
    });
  }

  async matchAgentToTask(
    task: AgentTask,
    availableAgents: AIAgent[]
  ): Promise<AgentCapabilityMatch[]> {
    const requiredCapabilities = this.extractRequiredCapabilities(task);
    const matches: AgentCapabilityMatch[] = [];

    for (const agent of availableAgents) {
      if (agent.status === 'idle' || agent.status === 'active') {
        const match = await this.calculateCapabilityMatch(agent, requiredCapabilities);
        matches.push(match);
      }
    }

    return matches.sort((a, b) => b.matchScore - a.matchScore);
  }

  // Cross-Agent Learning and Performance Benchmarking
  async updatePerformanceBenchmarks(agents: AIAgent[]): Promise<void> {
    for (const agent of agents) {
      const benchmark = await this.calculatePerformanceBenchmark(agent, agents);
      this.benchmarks.set(agent.id, benchmark);
    }
  }

  async getPerformanceRanking(
    specialization?: AgentSpecialization,
    taskType?: DecisionType
  ): Promise<PerformanceBenchmark[]> {
    let benchmarks = Array.from(this.benchmarks.values());

    if (specialization) {
      benchmarks = benchmarks.filter(b => b.specialization === specialization);
    }

    if (taskType) {
      benchmarks.sort((a, b) => {
        const rankA = a.ranking.byTaskType[taskType] || 0;
        const rankB = b.ranking.byTaskType[taskType] || 0;
        return rankA - rankB;
      });
    } else {
      benchmarks.sort((a, b) => a.ranking.overall - b.ranking.overall);
    }

    return benchmarks;
  }

  async identifyLearningOpportunities(agentId: string): Promise<CrossAgentLearning[]> {
    const agent = await this.getAgent(agentId);
    if (!agent) return [];

    const opportunities: CrossAgentLearning[] = [];
    const allLearnings = Array.from(this.learningTransfers.values());

    // Find relevant learnings from other agents
    for (const learning of allLearnings) {
      if (learning.sourceAgentId !== agentId && 
          this.isLearningRelevant(learning, agent)) {
        opportunities.push(learning);
      }
    }

    return opportunities.sort((a, b) => b.knowledge.effectiveness - a.knowledge.effectiveness);
  }

  // Private Helper Methods
  private generateId(): string {
    return `collab_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  private agentService?: any; // Reference to AIAgentService

  setAgentService(agentService: any): void {
    this.agentService = agentService;
  }

  private async getAvailableAgents(): Promise<AIAgent[]> {
    if (this.agentService) {
      return this.agentService.getAllAgents();
    }
    return [];
  }

  private async getAgent(agentId: string): Promise<AIAgent | null> {
    if (this.agentService) {
      return this.agentService.getAgent(agentId);
    }
    return null;
  }

  private async assignAgentRoles(
    collaborationId: string,
    agents: AgentCapabilityMatch[],
    capabilities: string[]
  ): Promise<void> {
    // Assign primary and secondary roles based on capabilities
    const collaboration = this.collaborations.get(collaborationId);
    if (!collaboration) return;

    // Create role assignments and notify agents
    for (const agent of agents) {
      const role = this.determineAgentRole(agent, capabilities);
      await this.sendCommunication('system', agent.agentId, {
        message: `Role assignment for collaboration: ${role}`,
        data: { collaborationId, role, capabilities: agent.availableCapabilities },
        priority: 'high',
        requiresResponse: false
      }, collaborationId);
    }
  }

  private async establishCoordinationProtocols(collaborationId: string): Promise<void> {
    const collaboration = this.collaborations.get(collaborationId);
    if (!collaboration) return;

    // Set up communication protocols, decision-making processes, etc.
    const protocols = {
      communicationFrequency: 'as_needed',
      decisionMaking: 'consensus',
      conflictResolution: 'automated',
      progressReporting: 'milestone_based'
    };

    await this.broadcastToCollaboration(collaborationId, 'system', {
      message: 'Coordination protocols established',
      data: protocols,
      priority: 'medium',
      requiresResponse: false
    });
  }

  private async initializeSharedWorkspace(collaborationId: string, testId: string): Promise<void> {
    // Create shared workspace for collaboration
    const _workspace = {
      testId,
      sharedData: {},
      documents: [],
      decisions: [],
      progress: {}
    };

    // Store workspace reference in collaboration
    const collaboration = this.collaborations.get(collaborationId);
    if (collaboration) {
      collaboration.sharedResources.push(`workspace_${collaborationId}`);
    }
  }

  private async processCommunication(communication: AgentCommunication): Promise<void> {
    // Process different types of communications
    switch (communication.type) {
      case 'request':
        await this.handleCommunicationRequest(communication);
        break;
      case 'response':
        await this.handleCommunicationResponse(communication);
        break;
      case 'notification':
        await this.handleCommunicationNotification(communication);
        break;
      case 'data_share':
        await this.handleDataShare(communication);
        break;
    }
  }

  private async handleCommunicationRequest(_communication: AgentCommunication): Promise<void> {
    // Route request to appropriate handler based on content
    // Implementation would handle different request types
  }

  private async handleCommunicationResponse(_communication: AgentCommunication): Promise<void> {
    // Process response and update relevant systems
  }

  private async handleCommunicationNotification(_communication: AgentCommunication): Promise<void> {
    // Process notification and update agent states
  }

  private async handleDataShare(_communication: AgentCommunication): Promise<void> {
    // Process shared data and make it available to relevant agents
  }

  private async findRelevantAgentsForKnowledge(_knowledge: CrossAgentLearning['knowledge']): Promise<string[]> {
    // Find agents that would benefit from this knowledge
    const relevantAgents: string[] = [];
    
    // Implementation would analyze agent capabilities and current tasks
    // to determine relevance
    
    return relevantAgents;
  }

  private classifyKnowledgeType(knowledge: CrossAgentLearning['knowledge']): CrossAgentLearning['learningType'] {
    // Classify knowledge based on content and context
    if (knowledge.description.includes('pattern')) return 'pattern';
    if (knowledge.description.includes('strategy')) return 'strategy';
    if (knowledge.description.includes('failure') || knowledge.description.includes('error')) return 'failure';
    return 'optimization';
  }

  private async updateLearningEffectiveness(learningId: string): Promise<void> {
    const learning = this.learningTransfers.get(learningId);
    if (!learning) return;

    // Calculate effectiveness based on adoption rates and validation results
    const adoptionCount = Object.values(learning.adoptionStatus)
      .filter(status => status === 'adopted' || status === 'adapted').length;
    
    const totalTargets = learning.targetAgentIds.length;
    const adoptionRate = adoptionCount / totalTargets;

    // Update effectiveness score
    learning.knowledge.effectiveness = adoptionRate * 100;
  }

  private async checkAllAgentsResponded(_decisionId: string): Promise<boolean> {
    // Check if all agents have provided input for the decision
    // Implementation would track responses
    return false;
  }

  private async resolveCollaborativeDecision(_decisionId: string): Promise<void> {
    // Aggregate all agent inputs and make final decision
    // Implementation would use various decision-making algorithms
  }

  private determineResolutionStrategy(
    conflictType: ConflictResolution['conflictType'],
    _agentIds: string[]
  ): ConflictResolution['resolutionStrategy'] {
    // Determine best resolution strategy based on conflict type and agents involved
    switch (conflictType) {
      case 'resource':
        return 'performance_based';
      case 'decision':
        return 'expertise_based';
      case 'priority':
        return 'voting';
      case 'capability':
        return 'expertise_based';
      case 'strategy':
        return 'performance_based';
      default:
        return 'human_arbitration';
    }
  }

  private async initiateConflictResolution(conflictId: string): Promise<void> {
    const conflict = this.conflicts.get(conflictId);
    if (!conflict) return;

    // Notify involved agents about the conflict
    for (const agentId of conflict.involvedAgents) {
      await this.sendCommunication('system', agentId, {
        message: `Conflict detected: ${conflict.description}`,
        data: { conflictId, conflictType: conflict.conflictType },
        priority: 'high',
        requiresResponse: true
      });
    }
  }

  private async resolveByVoting(_conflict: ConflictResolution): Promise<ConflictResolution['resolution']> {
    // Implement voting-based resolution
    return {
      decision: 'voting_result',
      reasoning: 'Resolved by majority vote',
      confidence: 0.8,
      timestamp: new Date(),
      resolvedBy: 'voting_system'
    };
  }

  private async resolveByExpertise(_conflict: ConflictResolution): Promise<ConflictResolution['resolution']> {
    // Implement expertise-based resolution
    return {
      decision: 'expert_decision',
      reasoning: 'Resolved by most qualified agent',
      confidence: 0.9,
      timestamp: new Date(),
      resolvedBy: 'expertise_system'
    };
  }

  private async resolveByPerformance(_conflict: ConflictResolution): Promise<ConflictResolution['resolution']> {
    // Implement performance-based resolution
    return {
      decision: 'performance_decision',
      reasoning: 'Resolved by best performing agent',
      confidence: 0.85,
      timestamp: new Date(),
      resolvedBy: 'performance_system'
    };
  }

  private async escalateToHuman(conflict: ConflictResolution): Promise<ConflictResolution['resolution']> {
    // Escalate to human arbitration
    conflict.status = 'escalated';
    return {
      decision: 'escalated',
      reasoning: 'Escalated to human arbitration',
      confidence: 1.0,
      timestamp: new Date(),
      resolvedBy: 'escalation_system'
    };
  }

  private async notifyConflictResolution(conflict: ConflictResolution): Promise<void> {
    // Notify all involved agents about the resolution
    for (const agentId of conflict.involvedAgents) {
      await this.sendCommunication('system', agentId, {
        message: `Conflict resolved: ${conflict.description}`,
        data: { conflictId: conflict.id, resolution: conflict.resolution },
        priority: 'medium',
        requiresResponse: false
      });
    }
  }

  private async calculateCapabilityMatch(
    agent: AIAgent,
    requiredCapabilities: string[],
    _constraints?: any
  ): Promise<AgentCapabilityMatch> {
    const availableCapabilities = agent.capabilities.map(cap => cap.id);
    const missingCapabilities = requiredCapabilities.filter(
      req => !availableCapabilities.includes(req)
    );

    // Calculate match score based on capability overlap and proficiency
    let matchScore = 0;
    let totalWeight = 0;

    for (const reqCap of requiredCapabilities) {
      const capability = agent.capabilities.find(cap => cap.id === reqCap);
      if (capability) {
        matchScore += capability.proficiency / 100;
        totalWeight += 1;
      }
    }

    matchScore = totalWeight > 0 ? matchScore / totalWeight : 0;

    // Adjust for current load
    const currentLoad = agent.currentTasks.length / (agent.configuration.constraints.maxConcurrentTasks || 5);

    return {
      agentId: agent.id,
      matchScore,
      availableCapabilities,
      missingCapabilities,
      currentLoad,
      estimatedContribution: matchScore * (1 - currentLoad),
      specialization: agent.specialization
    };
  }

  private extractRequiredCapabilities(task: AgentTask): string[] {
    // Extract required capabilities from task type and context
    const capabilityMap: { [key in DecisionType]: string[] } = {
      test_creation: ['test_design', 'statistical_planning'],
      variant_generation: ['content_optimization', 'design_variation'],
      winner_implementation: ['template_modification', 'deployment'],
      campaign_optimization: ['campaign_design', 'performance_analysis'],
      audience_targeting: ['audience_analysis', 'segmentation'],
      statistical_analysis: ['statistical_analysis', 'data_interpretation'],
      content_modification: ['content_optimization', 'copywriting'],
      resource_allocation: ['resource_management', 'optimization']
    };

    return capabilityMap[task.type] || [];
  }

  private async calculatePerformanceBenchmark(
    agent: AIAgent,
    allAgents: AIAgent[]
  ): Promise<PerformanceBenchmark> {
    // Calculate comprehensive performance benchmark
    const metrics = {
      successRate: agent.performance.overall.successRate,
      averageConfidence: agent.performance.overall.averageConfidence,
      taskCompletionTime: agent.performance.overall.averageTaskDuration,
      improvementRate: agent.performance.overall.improvementRate,
      collaborationEffectiveness: this.calculateCollaborationEffectiveness(agent)
    };

    // Calculate rankings
    const ranking = {
      overall: this.calculateOverallRanking(agent, allAgents),
      bySpecialization: this.calculateSpecializationRanking(agent, allAgents),
      byTaskType: this.calculateTaskTypeRankings(agent, allAgents)
    };

    // Calculate trends
    const trends = {
      period: 'weekly' as const,
      improvement: this.calculateImprovementTrend(agent),
      consistency: this.calculateConsistencyScore(agent)
    };

    return {
      agentId: agent.id,
      specialization: agent.specialization,
      metrics,
      ranking,
      trends,
      lastUpdated: new Date()
    };
  }

  private calculateCollaborationEffectiveness(_agent: AIAgent): number {
    // Calculate how effective the agent is in collaborations
    // This would analyze past collaboration outcomes
    return 0.8; // Placeholder
  }

  private calculateOverallRanking(agent: AIAgent, allAgents: AIAgent[]): number {
    // Calculate overall ranking among all agents
    const scores = allAgents.map(a => a.performance.overall.successRate);
    scores.sort((a, b) => b - a);
    return scores.indexOf(agent.performance.overall.successRate) + 1;
  }

  private calculateSpecializationRanking(agent: AIAgent, allAgents: AIAgent[]): number {
    // Calculate ranking within specialization
    const sameSpecialization = allAgents.filter(a => a.specialization === agent.specialization);
    const scores = sameSpecialization.map(a => a.performance.overall.successRate);
    scores.sort((a, b) => b - a);
    return scores.indexOf(agent.performance.overall.successRate) + 1;
  }

  private calculateTaskTypeRankings(agent: AIAgent, allAgents: AIAgent[]): { [taskType: string]: number } {
    // Calculate rankings for each task type
    const rankings: { [taskType: string]: number } = {};
    
    Object.keys(agent.performance.byTaskType).forEach(taskType => {
      const taskPerf = agent.performance.byTaskType[taskType as DecisionType];
      const allTaskPerfs = allAgents.map(a => a.performance.byTaskType[taskType as DecisionType]?.successRate || 0);
      allTaskPerfs.sort((a, b) => b - a);
      rankings[taskType] = allTaskPerfs.indexOf(taskPerf.successRate) + 1;
    });

    return rankings;
  }

  private calculateImprovementTrend(agent: AIAgent): number {
    // Calculate improvement trend over time
    const data = agent.performance.trends.data;
    if (data.length < 2) return 0;

    const recent = data.slice(-5); // Last 5 data points
    const older = data.slice(-10, -5); // Previous 5 data points

    if (older.length === 0) return 0;

    const recentAvg = recent.reduce((sum, d) => sum + d.successRate, 0) / recent.length;
    const olderAvg = older.reduce((sum, d) => sum + d.successRate, 0) / older.length;

    return recentAvg - olderAvg;
  }

  private calculateConsistencyScore(agent: AIAgent): number {
    // Calculate consistency of performance
    const data = agent.performance.trends.data;
    if (data.length < 3) return 1;

    const successRates = data.map(d => d.successRate);
    const mean = successRates.reduce((sum, rate) => sum + rate, 0) / successRates.length;
    const variance = successRates.reduce((sum, rate) => sum + Math.pow(rate - mean, 2), 0) / successRates.length;
    const stdDev = Math.sqrt(variance);

    // Return consistency score (lower standard deviation = higher consistency)
    return Math.max(0, 1 - stdDev);
  }

  private determineAgentRole(agent: AgentCapabilityMatch, capabilities: string[]): string {
    // Determine the primary role for the agent in the collaboration
    const primaryCapabilities = agent.availableCapabilities.filter(cap => 
      capabilities.includes(cap)
    );

    if (primaryCapabilities.length === 0) return 'support';
    
    // Return the role based on the agent's strongest capability
    return primaryCapabilities[0];
  }

  private isLearningRelevant(learning: CrossAgentLearning, agent: AIAgent): boolean {
    // Determine if a learning is relevant to an agent
    const agentCapabilities = agent.capabilities.map(cap => cap.id);
    const learningContext = learning.knowledge.applicability;

    return learningContext.some(context => agentCapabilities.includes(context));
  }
}